import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  Group,
  ScrollArea,
  Text,
  ActionIcon,
  Divider,
  Chip,
  Chips,
  Autocomplete,
  TextInput,
  SegmentedControl,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  Trash,
  Filter,
  Calendar,
  Search,
  Refresh,
  Select,
  Tooltip,
  Equal,
} from 'tabler-icons-react';
import throttle from 'lodash/throttle';
import { useDispatch, useSelector } from 'react-redux';
import { readOnly } from 'glamor';
import { getFieldDefCore } from '../../../hooks/useFieldDef';
import { expToDBQuery as expToDBQueryImpl } from '../../Utils/QueryBuilderUtils';
import ObjFieldListMulti from '../Objects/ObjFields/objFieldListMulti';
import { setViewFilters } from '../../../reducers/objectReducers/action';
import APortal from '../APortal/APortal';
import ObjectSelector from '../../object-selector';
import FilterConsoleConfigurator from './FilterConsoleConfigurator';
import { IconPreview } from '../../../constants/icon';
import { CtnFieldMulti, GrpFieldConsole } from './FilterConsoleCSS';
/**
 * FilterConsoleHorizontal (JS, not TS)
 * ---------------------------------------------------------------------------
 * One-line horizontal filter console.
 * Props
 *  - fieldsByType: Array<{ objTypeId: string, fieldNames: string[] }>
 *  - onChange: ({ filters: string[], filterRestriction: string[] }) => void
 *  - storageNamespace?: string  (default: perspectives.filterConsole.v1)
 *  - resolveFieldDef: (objTypeId, fieldName) => { type, isMulti, isLinked, options? }
 *  - listOptions: (objTypeId, fieldName) => [{ value, label }] | null
 *  - expToDBQuery?: (expressionObj) => string  // uses your QueryBuilder impl
 *  - i18n?: (key) => string (default pass-through)
 *  - preset?: { [objTypeId]: { [fieldName]: { op, value } } } // input takes priority over storage
 *  - onOpenObjectPicker?: (objTypeId, fieldName, onPick) => void // for linked fields
 *
 * Behaviour (confirmed):
 *  - Simple operators only (friendly): text contains/equals, list IN (ANY),
 *    date before/after, number equals, linked equals.
 *  - Grouping: AND within same objType.
 *  - Output strings must match QueryBuilder final syntax. If expToDBQuery isn't
 *    provided, we fallback to a conservative string builder.
 *  - Cross-view persistence in localStorage per (objTypeId, fieldName). Input
 *    preset has priority over stored values. A Reset button clears current
 *    fields and their storage entries.
 *  - Real-time onChange with light throttle.
 */

const NAMESPACE = 'perspectives.filterConsole.v1';
const isNonEmpty = (v) =>
  v !== undefined &&
  v !== null &&
  (typeof v === 'string' ? v.trim() !== '' : true);
const toISODate = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : d);

const defaultOpsForType = {
  text: ['contains', 'equals'],
  list: ['in'],
  date: ['before', 'after'],
  number: ['equals'],
  linked: ['equals'],
};

// Conservative fallback string builder when expToDBQuery is not injected
const fallbackToQueryString = (terms) => {
  const parts = terms
    .filter(
      (t) =>
        isNonEmpty(t.value) && !(Array.isArray(t.value) && t.value.length === 0)
    )
    .map((t) => {
      const f = t.field;
      const vArr = Array.isArray(t.value) ? t.value : [t.value];
      if (t.type === 'date') {
        if (t.op === 'before') return `${f} < '${toISODate(vArr[0])}'`;
        if (t.op === 'after') return `${f} > '${toISODate(vArr[0])}'`;
      }
      if (t.type === 'list') {
        const quoted = vArr
          .map((x) => `'${String(x).replace(/'/g, "\\'")}'`)
          .join(', ');
        return `${f} IN [${quoted}]`;
      }
      if (t.type === 'text') {
        if (t.op === 'contains')
          return `${f} =~ '.*${String(vArr[0]).replace(/'/g, "\\'")}.*'`;
        if (t.op === 'equals')
          return `${f} = '${String(vArr[0]).replace(/'/g, "\\'")}'`;
      }
      if (t.type === 'number') {
        const num = Number(vArr[0]);
        if (Number.isFinite(num)) return `${f} = ${num}`;
      }
      if (t.type === 'linked') {
        return `${f} = '${String(vArr[0]).replace(/'/g, "\\'")}'`;
      }
      return `${f} = '${String(vArr[0]).replace(/'/g, "\\'")}'`;
    });
  return parts.join(' AND ');
};

export default function FilterConsoleHorizontal({
  fieldsByType,
  onChange,
  storageNamespace = NAMESPACE,
  resolveFieldDef,
  listOptions,
  expToDBQuery,
  i18n = (k) => k,
  preset = null,
  onOpenObjectPicker,
}) {
  // Normalize sample typo {ObjTypeId}
  const normalized = useMemo(
    () =>
      (fieldsByType || []).map((g) => ({
        objTypeId: g.objTypeId || g.ObjTypeId,
        fieldNames: g.fieldNames || [],
      })),
    [fieldsByType]
  );
  const dispatch = useDispatch();
  // Local UI state: { [objTypeId]: { [fieldName]: { op, value, valueLabel? } } }
  const [stateMap, setStateMap] = useState({});
  const mountedRef = useRef(false);

  // Load cross-view storage, then merge with preset (preset wins)
  useEffect(() => {
    const next = {};
    normalized.forEach(({ objTypeId, fieldNames }) => {
      if (!objTypeId) return;
      next[objTypeId] = next[objTypeId] || {};
      fieldNames.forEach((fn) => {
        const key = `${storageNamespace}.${objTypeId}.${fn}`;
        let stored = null;
        try {
          stored = JSON.parse(localStorage.getItem(key));
        } catch (e) {}
        const presetVal = preset?.[objTypeId]?.[fn];
        const useVal = presetVal || stored || null;
        if (
          useVal &&
          (isNonEmpty(useVal.value) ||
            (Array.isArray(useVal.value) && useVal.value.length))
        ) {
          next[objTypeId][fn] = {
            op: useVal.op,
            value: useVal.value,
            valueLabel: useVal.valueLabel,
          };
        }
      });
    });
    setStateMap(next);
    mountedRef.current = true;
  }, [storageNamespace, JSON.stringify(normalized), JSON.stringify(preset)]);

  // Persist a single field to localStorage
  const persistField = (objTypeId, fieldName, val) => {
    const key = `${storageNamespace}.${objTypeId}.${fieldName}`;
    if (
      val &&
      (isNonEmpty(val.value) || (Array.isArray(val.value) && val.value.length))
    ) {
      localStorage.setItem(key, JSON.stringify(val));
    } else {
      localStorage.removeItem(key);
    }
  };

  // Build output whenever state changes (throttled notify)
  const notify = useMemo(
    () => throttle((payload) => onChange && onChange(payload), 120),
    [onChange]
  );

  useEffect(() => {
    if (!mountedRef.current) return;
    const filters = [];
    const filterRestriction = [];

    normalized.forEach(({ objTypeId, fieldNames }) => {
      const st = stateMap[objTypeId] || {};
      const terms = [];
      fieldNames.forEach((fn) => {
        const def = resolveFieldDef?.(objTypeId, fn) || {};
        const raw = st[fn];
        if (!raw) return;
        const val = raw.value;
        const op = raw.op || (def?.type === 'text' ? 'contains' : 'equals');
        if (!isNonEmpty(val) && !(Array.isArray(val) && val.length)) return;

        let type = def?.type;
        if (def?.isLinked) {
          type = 'linked';
        }
        if (type === 'date' && val) {
          const norm = Array.isArray(val) ? val[0] : val;
          terms.push({ field: fn, type: 'date', op, value: toISODate(norm) });
        } else if (type === 'list') {
          const arr = Array.isArray(val) ? val : [val];
          terms.push({
            field: fn,
            type: 'list',
            op: 'list_in',
            value: JSON.stringify(arr),
          });
        } else if (type === 'number') {
          terms.push({ field: fn, type: 'number', op: 'equals', value: val });
        } else if (type === 'linked') {
          terms.push({ field: fn, type: 'linked', op: 'equals', value: val });
        } else {
          terms.push({
            field: fn,
            type: 'text',
            op: op || 'contains',
            value: val,
          });
        }
      });

      if (terms.length) {
        filters.push(objTypeId);
        let str = '';
        if (expToDBQuery) {
          const expression = {
            type: 'expression',
            op: 'AND',
            terms: terms.map((t) => ({
              type: 'term',
              field: t.field,
              op: mapToQBOp(t),
              value: t.value,
              field_type: t.type,
            })),
          };
          try {
            str = expToDBQuery(expression) || '';
          } catch (e) {
            str = fallbackToQueryString(terms);
          }
        } else {
          str = fallbackToQueryString(terms);
        }
        if (str) filterRestriction.push(str);
      }
    });

    notify({ filters, filterRestriction });
  }, [
    JSON.stringify(stateMap),
    JSON.stringify(normalized),
    expToDBQuery,
    resolveFieldDef,
    notify,
  ]);

  const updateField = (objTypeId, fieldName, updater) => {
    setStateMap((prev) => {
      const curr = prev[objTypeId]?.[fieldName] || null;
      const nextVal = typeof updater === 'function' ? updater(curr) : updater; // helper: valeur “vide” ⇒ suppression
      const isEmptyVal =
        nextVal == null ||
        (!isNonEmpty(nextVal?.value) &&
          !(Array.isArray(nextVal?.value) && nextVal?.value.length));
      const next = { ...prev, [objTypeId]: { ...(prev[objTypeId] || {}) } };
      if (isEmptyVal) {
        // 1) enlever du localStorage
        const key = `${storageNamespace}.${objTypeId}.${fieldName}`;
        localStorage.removeItem(key);
        // 2) enlever du state
        delete next[objTypeId][fieldName];
        // 3) si plus rien pour cet objType, on peut nettoyer l’objet
        if (!Object.keys(next[objTypeId]).length) delete next[objTypeId];
      } else {
        // garder et persister
        next[objTypeId][fieldName] = nextVal;
        persistField(objTypeId, fieldName, nextVal);
      }
      return next;
    });
  };

  const clearAll = () => {
    const copy = { ...stateMap };
    normalized.forEach(({ objTypeId, fieldNames }) => {
      fieldNames.forEach((fn) => {
        const key = `${storageNamespace}.${objTypeId}.${fn}`;
        localStorage.removeItem(key);
        if (copy[objTypeId]) delete copy[objTypeId][fn];
      });
    });
    setStateMap(copy);
    dispatch(setViewFilters([], []));
  };

  return (
    <div
      style={{
        borderBottom: '1px solid var(--mantine-color-default-border)',
        padding: 6,
      }}
    >
      <ScrollArea
        type="auto"
        scrollHideDelay={0}
        styles={{ viewport: { overflowY: 'hidden' } }}
      >
        <Group
          gap={8}
          wrap="nowrap"
          align="flex-end"
          direction="row"
          style={{ paddingBlock: 8, whiteSpace: 'nowrap' }}
        >
          <Filter size={24} color={'black'} />
          <Text
            size="sm"
            style={{ fontWeight: 600, color: 'var(--ds-text-base)' }}
          ></Text>
          {normalized.map(({ objTypeId, fieldNames }) => (
            <Group key={objTypeId} gap={12} wrap="nowrap" align="flex-end">
              <Divider orientation="vertical" />
              {fieldNames.map((fieldName) => (
                <FieldFilter
                  key={`${objTypeId}.${fieldName}`}
                  objTypeId={objTypeId}
                  fieldName={fieldName}
                  value={stateMap[objTypeId]?.[fieldName] || null}
                  onChange={(val) => updateField(objTypeId, fieldName, val)}
                  resolveFieldDef={resolveFieldDef}
                  listOptions={listOptions}
                  i18n={i18n}
                  onOpenObjectPicker={onOpenObjectPicker}
                />
              ))}
            </Group>
          ))}
          <ActionIcon variant="subtle" onClick={clearAll} title="Reset">
            <Refresh size={16} />
          </ActionIcon>
          <FilterConsoleConfigurator iconOnly />
        </Group>
      </ScrollArea>
    </div>
  );
}

function mapToQBOp(term) {
  if (term.type === 'text') {
    if (term.op === 'contains') return 'text_contains'; // align to QB if it expects 'contains'
    if (term.op === 'equals') return 'text_is';
  }
  if (term.type === 'date') {
    if (term.op === 'before') return 'lt'; // adjust if your QB uses 'before'
    if (term.op === 'after') return 'gt';
  }
  if (term.type === 'list') return 'list_in';
  if (term.type === 'number') return 'is';
  if (term.type === 'linked') return 'is';
  return term.op || 'is';
}

function FieldFilter({
  objTypeId,
  fieldName,
  value,
  onChange,
  resolveFieldDef,
  listOptions,
  i18n,
  onOpenObjectPicker,
}) {
  const def = resolveFieldDef?.(objTypeId, fieldName) || {};
  let type = def?.type || 'text';

  console.log(JSON.stringify(def));
  let label = `${def.objectLabel}>${def?.label}` || 'xxxx';
  if (def?.isLinked) type = 'linked';

  const ops = defaultOpsForType[type] || ['equals'];
  const currentOp = value?.op || (type === 'text' ? 'contains' : ops[0]);

  const set = (partial) => {
    // 1) Cas suppression explicite
    if (partial == null) {
      onChange(null); // -> updateField supprimera l'entrée + storage
      return;
    }

    // 2) Savoir si les clés sont réellement passées (même si null)
    const hasValueKey = Object.prototype.hasOwnProperty.call(partial, 'value');
    const hasValueLabelKey = Object.prototype.hasOwnProperty.call(
      partial,
      'valueLabel'
    );

    const next = {
      op: partial.op ?? value?.op ?? currentOp,
      value: hasValueKey ? partial.value : value ? value.value : null,
      valueLabel: hasValueLabelKey
        ? partial.valueLabel
        : value
        ? value.valueLabel
        : '',
    };

    // 3) Si la valeur est vide, on déclenche une suppression propre
    const isEmpty =
      next.value == null ||
      (Array.isArray(next.value) && next.value.length === 0) ||
      (typeof next.value === 'string' && next.value.trim() === '');

    if (isEmpty) {
      onChange(null); // purge état + localStorage (via updateField)
    } else {
      onChange(next);
    }
  };

  return (
    <div style={{ minWidth: 100 }}>
      <Text size="xs" style={{ marginBottom: 4, color: 'var(--ds-text-base)' }}>
        {labelForField(i18n, objTypeId, label)}
      </Text>

      <GrpFieldConsole>
        {type === 'text' && (
          <Autocomplete
            size="xs"
            placeholder="…"
            value={value?.value || ''}
            onChange={(v) => set({ value: v })}
            data={(def?.options || [])
              .map((o) => (typeof o === 'string' ? o : o?.label ?? o?.value))
              .filter(Boolean)}
            limit={25}
          />
        )}

        {type === 'list' && (
          <CtnFieldMulti>
            <ObjFieldListMulti
              keyParam={`${objTypeId}`}
              attrName={fieldName}
              readOnly={false}
              options={def?.options}
              values={value?.value}
              unit={''}
              isModal={false}
              onChangeValues={(arr) => set({ value: arr })}
            />
          </CtnFieldMulti>
        )}

        {type === 'date' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {ops.length > 1 ? null : <Calendar size={14} />}
            {/* <DatePickerInput
            value={value?.value ? new Date(value.value) : null}
            onChange={(d) => set({ value: d ? toISODate(d) : null })}
            valueFormat="YYYY-MM-DD"
            size="xs"
            clearable
          /> */}
          </div>
        )}

        {type === 'number' && (
          <TextInput
            size="xs"
            type="number"
            value={isNonEmpty(value?.value) ? value.value : ''}
            onChange={(e) => set({ value: e.currentTarget.value })}
          />
        )}

        {type === 'linked' && (
          <LinkedPicker
            objTypeId={objTypeId}
            fieldName={fieldName}
            value={value}
            set={set}
            def={def}
          />
        )}

        {ops.length > 1 && (
          <SegmentedControl
            value={currentOp}
            onChange={(op) => set({ op })}
            data={ops.map((o) => ({ value: o, label: opToIcon(o) }))}
            size="xs"
            radius="sm"
            // label={OpsToIcon(currentOp)}
          />
        )}
      </GrpFieldConsole>
    </div>
  );
}

const opToIcon = (op) => {
  switch (op) {
    case 'text_contains':
    case 'contains':
      return <Search size={13} />;
    case 'text_equals':
    case 'equals':
      return <Equal size={13} />;
    default:
      return <Search size={13} />;
  }
};

function labelForField(i18n, objTypeId, fieldName) {
  const k = `field.${objTypeId}.${fieldName}`;
  const t = i18n(k);
  return t === k ? fieldName : t;
}

// ---------------------------------------------------------------------------
// Adapters (drop-in) — concrete impls you asked for (JS, no TS)
// Put this in a parent that has access to objectsDefList (Redux or prop)
// and pass the functions to <FilterConsoleHorizontal/>.
// ---------------------------------------------------------------------------

// Adjust these imports to your project structure

export function FilterConsoleBound({
  fieldsByType,
  preset,
  i18n,
  onChange,
  onOpenObjectPicker,
  storageNamespace,
}) {
  // 1) Read the object definitions once here (outside the console)
  const objectsDefList = useSelector(
    (s) => s.objects?.objectsDefList || s.objectsDefList || {}
  );

  // 2) Your requested functions — stable via memo/callback
  const resolveFieldDef = useMemo(() => {
    return (objTypeId, fieldName) => {
      const def =
        getFieldDefCore(objectsDefList, { objectType: objTypeId, fieldName }) ||
        {};
      // Derive a minimal shape expected by the console
      let type = def.type;
      if (!type && def.format === 'date') type = 'date';
      if (!type) type = 'text';
      return {
        type,
        label: def?.label,
        objectLabel: def.effObjectLabel,
        isLinked: !!def.isLinked,
        options: def.parsedOptions || def.options || [],
      };
    };
  }, [objectsDefList]);

  const listOptions = useMemo(() => {
    return (objTypeId, fieldName) => {
      const def =
        getFieldDefCore(objectsDefList, { objectType: objTypeId, fieldName }) ||
        {};
      const options = def.parsedOptions || def.options || [];
      return options.map((o) => ({
        value: o?.value ?? o?.id ?? o,
        label: o?.display ?? o?.label ?? String(o?.value ?? o),
      }));
    };
  }, [objectsDefList]);

  const expToDBQuery = useCallback((expressionObj) => {
    // Route to your real QueryBuilder to guarantee identical syntax
    return typeof expToDBQueryImpl === 'function'
      ? expToDBQueryImpl(expressionObj)
      : '';
  }, []);

  const openObjectPicker = useCallback((objTypeId, fieldName, onPick) => {
    // Hook this into your existing modal. Example:
    // openAssigneeModal({ onSelect: (u) => onPick({ id: u.id, label: u.name }) });
    console.warn('openObjectPicker not wired:', {
      objTypeId,
      fieldName,
      onPick,
    });
  }, []);

  return (
    <FilterConsoleHorizontal
      fieldsByType={fieldsByType}
      preset={preset}
      i18n={i18n}
      onChange={onChange}
      onOpenObjectPicker={onOpenObjectPicker}
      storageNamespace={storageNamespace}
      resolveFieldDef={resolveFieldDef}
      listOptions={listOptions}
      expToDBQuery={expToDBQuery}
    />
  );
}

function LinkedPicker({ objTypeId, fieldName, value, set, def }) {
  const [open, setOpen] = useState(false);

  const targetType = useMemo(() => {
    return def?.type || objTypeId;
  }, [def, objTypeId]);

  // id d’ancre unique et stable
  const anchorId = useMemo(
    () => `portal-anchor-${objTypeId}-${fieldName}`,
    [objTypeId, fieldName]
  );

  // sélection finale depuis l’ObjectSelector
  const handleSelect = useCallback(
    (picked) => {
      if (!picked) return;
      set({
        op: 'equals',
        value: picked.id,
        valueLabel: picked.label,
      });
      setOpen(false);
    },
    [set]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <TextInput
        size="xs"
        value={value?.valueLabel ?? ''}
        readOnly
        placeholder="Pick…"
        rightSection={<Search size={14} />}
        onClick={() => setOpen(true)}
      />
      {isNonEmpty(value?.value) && (
        <ActionIcon
          size="sm"
          variant="subtle"
          onClick={() => set(null)}
          title="Clear"
        >
          <Trash size={14} />
        </ActionIcon>
      )}

      {/* Ancre pour le portail (ne prend pas de place en layout) */}
      <span id={anchorId} style={{ position: 'relative' }} />

      {/* Portail lié à l’ancre ; s’ouvre/ferme localement */}
      {open && (
        <APortal idParent={anchorId} onClose={() => setOpen(false)}>
          <div
            style={{
              background: 'var(--mantine-color-body)',
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 8,
              padding: 8,
              width: 360,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}
          >
            <ObjectSelector
              typeSelectedObject={targetType}
              isDisplaySummary={false}
              isDisplayTitle={false}
              isDisplayConfirm={false}
              height={360}
              onSelectValue={(pickedId, pickedLabel) =>
                handleSelect({
                  id: pickedId,
                  label: String(pickedLabel ?? pickedId),
                })
              }
              onCancel={() => setOpen(false)}
            />
          </div>
        </APortal>
      )}
    </div>
  );
}
